/**
 * Resolve a localized name map to a single display string.
 *
 * Mirrors the fallback *shape* already used in `app/[locale]/produkti/page.tsx`
 * (`localizedName`): try a priority-ordered list of keys, then fall back to
 * whatever value in the map is populated first, in insertion order.
 *
 * `priorityKeys` is a parameter rather than a hardcoded `[locale, "sl", "en"]`
 * because the key used for Slovenian differs by data source. Admin-entered
 * `GetProductDto.name` (product-store) keys Slovenian as `"si"` — see
 * `app/admin/product/edit/[id]/page.tsx`, which filters `"si"` out of its
 * translation-target list because it's always the default/primary key — while
 * `produkti/page.tsx`'s design-definition names use next-intl's raw locale,
 * `"sl"`. Hardcoding `"sl"` here would silently stop matching Slovenian
 * product names for every non-Slovenian visitor to the product store.
 * Callers build their own priority list, e.g.
 * `resolveLocalizedName(product.name, [language, "si", "en"])`.
 *
 * Unlike a plain `??` chain, an empty string at any priority key does NOT
 * win — `resolveLocalizedName({ it: "", si: "Skodelica" }, ["it", "si"])`
 * returns `"Skodelica"`, not `""`. A product's name map should always have
 * at least one real value; showing a visitor someone else's populated name
 * beats showing them nothing.
 *
 * Returns `""` only when `name` is missing or every value in it is empty —
 * callers that need a guaranteed non-empty string (e.g. image `alt` text,
 * where `alt=""` marks the image decorative) must supply their own fallback
 * on top of this, since a generic placeholder isn't this function's job.
 */
export function resolveLocalizedName(
  name: Record<string, string> | null | undefined,
  priorityKeys: string[]
): string {
  if (!name || typeof name !== "object") return "";

  for (const key of priorityKeys) {
    const candidate = name[key];
    if (typeof candidate === "string" && candidate.length > 0) {
      return candidate;
    }
  }
  for (const candidate of Object.values(name)) {
    if (typeof candidate === "string" && candidate.length > 0) {
      return candidate;
    }
  }
  return "";
}
